import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import type { DeliveryOptionSelection } from "@types_def/models/delivery.types";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import CustomModal from "@/components/common/CustomModal";
import PdfPreview from "@/components/common/pdfPreview";
import { useUserContext } from "@/contexts/UserContext/index.context";
import { updateDeliveryDetail } from "@/features/offers/cache";
import { isHeicUrl, useHeicImage } from "@/hooks/useHeicImage";
import { useUpdateDeliveryOptionSelectionMutation } from "@/libs/generated/hooks";
import { toEuro } from "@/utils/format";

type AdditionalOptionsDisplayProps = {
  options: DeliveryOptionSelection[];
  deliveryId: number;
  editable?: boolean;
};

/** Renders an image (with loader) or a PDF preview based on URL extension. */
function FilePreview({ url }: { url: string }) {
  const isHeic = isHeicUrl(url);
  const { convertedUrl, error } = useHeicImage(url);

  if (error) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Aperçu non disponible
        </Typography>
      </Box>
    );
  }

  // Non-HEIC files render the original URL directly — no conversion, no loader.
  const displayUrl = convertedUrl ?? (!isHeic ? url : null);

  if (!displayUrl) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          minHeight: 120,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={displayUrl}
      alt="Option file"
      sx={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain", borderRadius: 1 }}
    />
  );
}

const AdditionalOptionsDisplay: React.FC<AdditionalOptionsDisplayProps> = ({
  options,
  deliveryId,
  editable = true,
}) => {
  const { t } = useTranslation();
  const { userState } = useUserContext();
  if (!userState) return null;
  const isAdmin = userState["custom:role"] === "ADMIN";
  const [filePreviewUrls, setFilePreviewUrls] = useState<string[]>([]);
  const [filePreviewOpen, setFilePreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  if (!options || options.length === 0) {
    return (
      <Typography variant="body1" color="gray">
        {t("delivery.summary.no-options")}
      </Typography>
    );
  }

  const getFileCount = (fileString: string | null | undefined) => {
    if (!fileString) return 0;
    return fileString.split(",").filter(Boolean).length;
  };
  return (
    <>
      <Stack spacing={0.75}>
        {options.map((option) => {
          const fileCount = getFileCount(option.files);
          const fileUrls = option.files?.split(",").filter(Boolean) ?? [];

          return (
            <Box
              key={option.id}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 0.75,
                p: 1,
                //bgcolor: "background.paper",
                transition: "border-color 0.2s",
                "&:hover": {
                  borderColor: "primary.light",
                },
              }}
            >
              {/* Header Row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>
                  {option.name || t("delivery.summary.unnamed-option")}
                </Typography>

                {isAdmin && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {fileCount > 0 && (
                      <Tooltip title={t("delivery.summary.view-files", "View files")}>
                        <Badge
                          badgeContent={fileCount > 1 ? fileCount : 0}
                          color="primary"
                          sx={{
                            "& .MuiBadge-badge": { fontSize: 9, minWidth: 14, height: 14, p: 0 },
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              setFilePreviewUrls(fileUrls);
                              setPreviewIndex(0);
                              setFilePreviewOpen(true);
                            }}
                            sx={{ p: 0.3, color: "primary.main" }}
                          >
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Badge>
                      </Tooltip>
                    )}
                    {editable && isAdmin && option.id && (
                      <EditOptionModal option={option} deliveryId={deliveryId} />
                    )}
                  </Box>
                )}
              </Box>

              {/* Info - Compact inline layout */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {option.clientPrice != null ? (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: 10,
                          lineHeight: 1,
                        }}
                      >
                        {t("delivery.summary.client-price", "Client:")}
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color="primary.main"
                        sx={{ fontSize: 11, lineHeight: 1 }}
                      >
                        {toEuro(option.clientPrice)}
                      </Typography>
                    </>
                  ) : (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: 11, lineHeight: 1 }}
                    >
                      {t("deliveryOption.pricing.pendingReceipt")}
                    </Typography>
                  )}

                  {isAdmin && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: 10,
                          lineHeight: 1,
                        }}
                      >
                        {t("delivery.summary.driver", "Driver:")}
                      </Typography>

                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color={option.driverReportedAmount ? "success.main" : "error.main"}
                        sx={{
                          fontSize: 11,
                          lineHeight: 1,
                        }}
                      >
                        {option.driverReportedAmount ? toEuro(option.driverReportedAmount) : "N/A"}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Stack>
      <Modal
        open={filePreviewOpen}
        onClose={() => setFilePreviewOpen(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            outline: "none",
            width: 680,
            height: 750,
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.25,
              borderBottom: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
            }}
          >
            {filePreviewUrls.length > 1 ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {previewIndex + 1} / {filePreviewUrls.length}
                </Typography>
              </Box>
            ) : (
              <Box /> // spacer to keep close button right-aligned
            )}
            <IconButton size="small" onClick={() => setFilePreviewOpen(false)}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* ── Content ── */}
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              bgcolor: "grey.100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {filePreviewUrls[previewIndex]?.toLowerCase().endsWith(".pdf") ? (
              <Box sx={{ width: "100%", height: "100%", overflow: "auto" }}>
                <PdfPreview url={filePreviewUrls[previewIndex]} />
              </Box>
            ) : (
              <FilePreview url={filePreviewUrls[previewIndex]} />
            )}
          </Box>

          {/* ── Footer nav — only when multiple files ── */}
          {filePreviewUrls.length > 1 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1,
                borderTop: "1px solid",
                borderColor: "divider",
                flexShrink: 0,
              }}
            >
              <IconButton
                size="small"
                disabled={previewIndex === 0}
                onClick={() => setPreviewIndex((i) => i - 1)}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography variant="caption" color="text.secondary">
                {previewIndex + 1} / {filePreviewUrls.length}
              </Typography>
              <IconButton
                size="small"
                disabled={previewIndex === filePreviewUrls.length - 1}
                onClick={() => setPreviewIndex((i) => i + 1)}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default AdditionalOptionsDisplay;

function EditOptionModal({
  option,
  deliveryId,
}: {
  option: DeliveryOptionSelection;
  deliveryId: number;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [driverReportedAmount, setDriverReportedAmount] = useState(
    option.driverReportedAmount?.toString() || ""
  );
  const name = option.name || "";

  const mutation = useUpdateDeliveryOptionSelectionMutation();

  const confirmAction = async () => {
    try {
      const result = await mutation.mutateAsync({
        updateInput: {
          selectionId: option.id as number,
          driverReportedAmount: driverReportedAmount ? Number(driverReportedAmount) : undefined,
        },
      });

      updateDeliveryDetail(queryClient, deliveryId, (current) => ({
        ...current,
        findDeliveryById: {
          ...current.findDeliveryById,
          selectedOptions:
            current.findDeliveryById.selectedOptions?.map((opt) =>
              opt.id === option.id ? result.updateDeliveryOptionSelection : opt
            ) ?? [],
        },
      }));

      toast.success(t("success", "Success"));
    } catch (err: any) {
      toast.error(err.message || "Error updating option");
    }
  };

  return (
    <CustomModal
      title={t("delivery.summary.edit-option", "Edit option")}
      confirmText={t("save", "Save")}
      cancelText={t("cancel", "Cancel")}
      button={
        <Tooltip title={t("delivery.summary.edit-option", "Edit option")}>
          <IconButton size="small" sx={{ p: 0.25 }}>
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      }
      confirmAction={confirmAction}
    >
      <Stack spacing={2} sx={{ mt: 2, minWidth: 300 }}>
        <TextField
          label={t("delivery.summary.display-name", "Display Name")}
          type="text"
          value={name}
          disabled
          fullWidth
        />
        <TextField
          label={t("delivery.summary.driver-reported-amount", "Driver Reported Amount")}
          type="number"
          value={driverReportedAmount}
          onChange={(e) => setDriverReportedAmount(e.target.value)}
          fullWidth
        />
      </Stack>
    </CustomModal>
  );
}
